<template>
    <card v-if="taskListTemplate" title="Aktiviteter">
        <template slot="actions">
            <card-cog v-if="! editingOrder && ! creatingNew && taskListTemplate.tasks.length > 0">
                <li>
                    <button @click="editingOrder = ! editingOrder">
                        <font-awesome-icon icon="arrows-alt" />
                        Ändra ordning
                    </button>
                </li>
            </card-cog>

            <button
                v-if="! creatingNew && ! editingOrder"
                class="btn btn-sm is-primary"
                @click="creatingNew = true"
            >
                Skapa ny
            </button>

            <button
                v-if="editingOrder"
                class="btn btn-sm is-ghost"
                @click="editingOrder = false"
            >
                Sluta ändra ordning
            </button>
        </template>
        <div v-if="creatingNew" class="mb-md bg-neutral-lightest rounded p-lg">
            <div v-if="! newTaskType">
                <select-input
                    v-model="newTaskType"
                    name="new_task_type"
                    :options="{ checkbox: 'Kryssruta', text: 'Fritextfält', number: 'Nummerfält', select: 'Select', image: 'Bildväljare', 'multi-select': 'Flervalsväljare', 'file': 'Filväljare' }"
                    placeholder="Välj typ av aktivitet"
                />

                <div class="button-group">
                    <button class="btn btn-md is-ghost" @click="creatingNew = false">
                        Avbryt
                    </button>

                    <button class="btn btn-md is-primary" disabled>
                        Skapa
                    </button>
                </div>
            </div>

            <div v-else>
                <component
                    :is="`create-${newTaskType}-task`"
                    :task-list-template="taskListTemplate"
                    @added="loadTasks"
                    @cancel="cancelCreating"
                />
            </div>
        </div>

        <div v-if="taskListTemplate.tasks.length > 0">
            <draggable
                v-model="taskListTemplate.tasks"
                :options="{ disabled: ! editingOrder }"
                @input="updatePositions"
            >
                <div v-for="task in taskListTemplate.tasks" :key="task.id">
                    <toggler
                        inline-template
                        :data="{ editingOrder: editingOrder, taskListTemplate: taskListTemplate }"
                        @deleted="loadTasks"
                    >
                        <div
                            :class="{ 'cursor-pointer': ! toggled && ! data.editingOrder, 'cursor-move': data.editingOrder }"
                            class="border border-neutral-light mt-md relative"
                        >
                            <div class="flex p-md items-center" @click="toggle">
                                <div>
                                    <div
                                        class="bg-neutral-lightest text-xs p-sm text-neutral-darkest rounded-full mr-sm inline-block text-center"
                                        style="width: 85px;"
                                    >
                                        {{ task.label }}
                                    </div>
                                    <span class="font-semibold">{{ task.body }}</span>
                                </div>

                                <div class="ml-auto">
                                    <div v-if="data.editingOrder" class="handle">
                                        <font-awesome-icon icon="arrows-alt" />
                                    </div>
                                    <button
                                        v-else-if="! toggled"
                                        class="btn-circle"
                                    >
                                        <font-awesome-icon icon="pencil" size="sm" />
                                    </button>
                                </div>
                            </div>

                            <div v-if="toggled" class="p-md">
                                <component
                                    :is="`edit-${task.type}-task`"
                                    :task="task"
                                    :task-list-template="data.taskListTemplate"
                                    @cancel="toggled = false"
                                    @deleted="$emit('deleted')"
                                />
                            </div>
                        </div>
                    </toggler>
                </div>
            </draggable>
        </div>
        <em v-else-if="! creatingNew">
            Aktivitetslistan saknar uppgifter, börja med att lägga till en.
        </em>
    </card>
</template>
<script>
    import CreateCheckboxTask from './CreateTaskTemplates/CreateCheckboxTask';
    import CreateTextTask from './CreateTaskTemplates/CreateTextTask';
    import CreateNumberTask from './CreateTaskTemplates/CreateNumberTask';
    import CreateSelectTask from './CreateTaskTemplates/CreateSelectTask';
    import CreateImageTask from './CreateTaskTemplates/CreateImageTask';
    import CreateFileTask from './CreateTaskTemplates/CreateFileTask';
    import CreateMultiSelectTask from './CreateTaskTemplates/CreateMultiSelectTask';

    import draggable from 'vuedraggable';

    export default {
        components: {
            CreateCheckboxTask, // eslint-disable-line vue/no-unused-components
            CreateTextTask, // eslint-disable-line vue/no-unused-components
            CreateNumberTask, // eslint-disable-line vue/no-unused-components
            CreateSelectTask, // eslint-disable-line vue/no-unused-components
            CreateImageTask, // eslint-disable-line vue/no-unused-components
            CreateMultiSelectTask, // eslint-disable-line vue/no-unused-components
            CreateFileTask, // eslint-disable-line vue/no-unused-components
            draggable,
        },
        props: {
            taskListTemplateId: {
                required: true,
                type: Number,
            },
        },
        data() {
            return {
                taskListTemplate: null,
                newTaskType: '',
                creatingNew: false,
                editingOrder: false,
            };
        },
        created() {
            this.loadTasks();

            EventBus.$on('task-deleted', () => {
                this.loadTasks();
            });
        },
        methods: {
            async loadTasks(options) {
                try {
                    const results = await axios.get(route('api.task-list-templates.show', this.taskListTemplateId));

                    this.taskListTemplate = results.data;

                    if (options !== undefined && ! options.keepCreateView) {
                        this.newTaskType = '';
                    }
                } catch (e) {
                    console.error(e);
                    flash_error('Kunde inte ladda listan, ladda om sidan och försök igen.');
                }
            },
            cancelCreating() {
                this.newTaskType = '';
                this.creatingNew = false;
            },
            async removeTask(task) {
                try {
                    await axios.delete(route('api.task-templates.destroy', task.id));

                    flash_success('Uppgiften borttagen!');
                } catch (e) {
                    flash_error('Kunde inte ta bort uppgiften, försök gärna igen!');
                }

                this.loadTasks();
            },
            async updatePositions(objects) {
                objects.forEach((obj, key) => {
                    obj.position = key;
                });

                try {
                    await axios.patch(route('api.task-templates.reorder', this.taskListTemplate.id), objects);
                    flash_success('Ordning uppdaterad!');
                } catch (e) {
                    flash_error('Kunde inte uppdatera ordningen');
                }
            },
        },
    };
</script>